import React, { useEffect, useRef, useState } from 'react';
import { Navbar, Nav, Container, Spinner } from 'react-bootstrap';
import { useLocation, useNavigate } from 'react-router-dom';
import axios from 'axios';
import '../components-css/Header.css';

const SECTIONS = [
  { id: '#quienes-somos', label: 'Quiénes somos' },
  { id: '#actividades', label: 'Actividades' },
  { id: '#en-que-creemos', label: 'En qué creemos' },
  { id: '#mensajes', label: 'Mensajes' },
  { id: '#visitanos', label: 'Visitanos' }
];

const Header = () => {
  const [menu, setMenu] = useState(null);
  const [loading, setLoading] = useState(true);
  const [expanded, setExpanded] = useState(false);
  const [activeSection, setActiveSection] = useState('#quienes-somos');
  const [indicatorStyle, setIndicatorStyle] = useState({ left: 0, width: 0, opacity: 0 });
  const location = useLocation();
  const navigate = useNavigate();
  const navRef = useRef(null);
  const headerRef = useRef(null);

  useEffect(() => {
    const updateHeaderHeight = () => {
      const headerEl = document.querySelector('.main-header');
      if (!headerEl) return;
      const height = headerEl.offsetHeight || 0;
      document.documentElement.style.setProperty('--header-height', `${height}px`);
      document.documentElement.style.setProperty('--anchor-offset', '-18px');
    };

    updateHeaderHeight();
    window.addEventListener('resize', updateHeaderHeight);
    return () => window.removeEventListener('resize', updateHeaderHeight);
  }, []);

  useEffect(() => {
    axios.get('/api/menu.json')
      .then(res => setMenu(res.data))
      .catch(() => setMenu(null))
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => {
    if (location.pathname !== '/') return;

    const updateActiveSection = () => {
      const styles = getComputedStyle(document.documentElement);
      const headerHeight = parseInt(styles.getPropertyValue('--header-height'), 10) || 0;
      const anchorOffset = parseInt(styles.getPropertyValue('--anchor-offset'), 10) || 0;
      const offset = headerHeight + anchorOffset + 4;
      const scrollPos = window.scrollY + offset;

      let currentSection = SECTIONS[0]?.id || '#quienes-somos';

      SECTIONS.forEach(section => {
        const el = document.querySelector(section.id);
        if (!el) return;
        const top = el.offsetTop;
        const bottom = top + el.offsetHeight;
        if (scrollPos >= top && scrollPos < bottom) {
          currentSection = section.id;
        }
      });

      setActiveSection(prev => (prev === currentSection ? prev : currentSection));
    };

    const onScroll = () => window.requestAnimationFrame(updateActiveSection);
    updateActiveSection();
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, [location.pathname]);

  useEffect(() => {
    if (location.pathname !== '/') return;
    if (location.hash) {
      const match = SECTIONS.find(section => section.id === location.hash);
      if (match) {
        setActiveSection(match.id);
      }
    }
  }, [location.hash, location.pathname]);

  useEffect(() => {
    const updateIndicator = () => {
      const navEl = navRef.current;
      if (!navEl) return;

      const isDesktop = window.matchMedia('(min-width: 992px)').matches;
      const activeLink = navEl.querySelector(`[data-section="${activeSection}"]`);

      if (!isDesktop || !activeLink || expanded) {
        setIndicatorStyle(prev => ({ ...prev, opacity: 0 }));
        return;
      }

      const navRect = navEl.getBoundingClientRect();
      const linkRect = activeLink.getBoundingClientRect();
      const left = linkRect.left - navRect.left;
      const width = linkRect.width;

      setIndicatorStyle({ left, width, opacity: 1 });
    };

    updateIndicator();
    window.addEventListener('resize', updateIndicator);
    return () => window.removeEventListener('resize', updateIndicator);
  }, [activeSection, expanded]);

  useEffect(() => {
    if (!expanded) return;

    const handlePointerDown = (event) => {
      const headerEl = headerRef.current;
      if (!headerEl) return;
      if (!headerEl.contains(event.target)) {
        setExpanded(false);
      }
    };

    document.addEventListener('pointerdown', handlePointerDown);
    return () => document.removeEventListener('pointerdown', handlePointerDown);
  }, [expanded]);

  if (loading) return <div className="text-center my-3"><Spinner animation="border" /></div>;
  if (!menu) return <div className="text-danger text-center my-3">Menu failed to load.</div>;

  // Cambia el fondo a gris claro (gris del proyecto original fuera de canaldevida-react)
  const handleNavClick = (event, targetId) => {
    event.preventDefault();
    setActiveSection(targetId);
    if (location.pathname !== '/') {
      setExpanded(false);
      navigate('/', { state: { scrollTo: targetId } });
      return;
    }
    const targetEl = document.querySelector(targetId);
    if (!targetEl) return;

    const styles = getComputedStyle(document.documentElement);
    const headerHeight = parseInt(styles.getPropertyValue('--header-height'), 10) || 0;
    const anchorOffset = parseInt(styles.getPropertyValue('--anchor-offset'), 10) || 0;
    const offset = headerHeight + anchorOffset;

    const targetTop = targetEl.getBoundingClientRect().top + window.scrollY - offset;
    window.scrollTo({ top: targetTop, behavior: 'smooth' });
    window.history.pushState(null, '', targetId);
    setExpanded(false);
  };

  return (
    <Navbar expand="lg" className="main-header" expanded={expanded} ref={headerRef}>
      <Container fluid>
        <Navbar.Brand href="/" className="navbar-brand">
          <img src="/assets/images/resources/logo-cdv-grande.png" alt="Logo Canal de Vida" height={50} />
        </Navbar.Brand>
        <Navbar.Toggle
          aria-controls="main-navbar-nav"
          className="navbar-toggler"
          onClick={() => setExpanded(prev => !prev)}
        />
        <Navbar.Collapse id="main-navbar-nav">
          <Nav className="me-auto navbar-nav" activeKey={activeSection} ref={navRef}>
            {SECTIONS.map(section => (
              <Nav.Link
                key={section.id}
                href={section.id}
                eventKey={section.id}
                data-section={section.id}
                onClick={(e) => handleNavClick(e, section.id)}
              >
                {section.label}
              </Nav.Link>
            ))}
            <span
              className="nav-indicator"
              style={{
                transform: `translateX(${indicatorStyle.left}px)`,
                width: `${indicatorStyle.width}px`,
                opacity: indicatorStyle.opacity
              }}
            />
          </Nav>
        </Navbar.Collapse>
      </Container>
    </Navbar>
  );
};

export default Header;
